import React, { useCallback } from "react";
import { useMutation } from "@apollo/client";
import { useFlag } from "@unleash/proxy-client-react";
import { useTranslation } from "react-i18next";

import { AddToBoxOrCartSuccessMessage } from "../components/atoms/AddToBoxOrCartSuccessMessage";
import { useAppContext, useLayoutContext } from "../components/organisms/Layout/context";
import { ADD_ITEM_TO_LOCAL_SHOPPING_CART } from "../graphql/mutations";
import { AddItemToLocalShoppingCart } from "../types/__generated__/AddItemToLocalShoppingCart";
import { showErrorAlert, showSuccessAlert } from "./alerts";
import { isLoggedIn } from "./auth";
import { releaseBuildYourOwnReservation, useBuildYourOwnAddGuard } from "./buildYourOwnCartLock";
import { I18N_NAMESPACES, UNLEASH_GUEST_CHECKOUT } from "./constants";
import { hasGuestBoxIntent, hasGuestChosenDirectAdds } from "./guestCheckout";
import { logError } from "./logging";

/**
 * The product fields a guest's local cart line is built from. Matches the ProductFragment shape
 * (collections cards, product details) as well as the lighter shapes other add buttons pass.
 */
export interface GuestAddableProduct {
  id: number | null;
  name: string;
  slug?: string | null;
  sellingPrice?: number | null;
  hasStock?: boolean | null;
  coverPhotoSmall?: string | null;
  coverPhotoSmallPng?: string | null;
  productType?: { id: number | null; canCountTowardsOrderMinimum?: boolean | null } | null;
}

export interface LocalCartItemInputOptions {
  product: GuestAddableProduct;
  quantity: number;
  extraInfo?: Record<string, unknown> | null;
  minBottlesForFreeShipping: number | null | undefined;
  standardShippingFee: number | null | undefined;
}

export interface LocalCartAddVariables {
  input: {
    minBottlesForFreeShipping: number | null | undefined;
    standardShippingFee: number | null | undefined;
    item: {
      quantity: number;
      product: {
        id: number | null;
        name: string;
        slug: string;
        sellingPrice: number | null;
        coverPhotoSmall: string | null;
        productType: { id: number | null; canCountTowardsOrderMinimum: boolean | null };
      };
      extraInfo: Record<string, unknown> | null;
    };
  };
}

/**
 * The one definition of a guest local cart line. Builds the ADD_ITEM_TO_LOCAL_SHOPPING_CART
 * variables from any product shape: only the fields the local cart stores are copied (so a raw
 * Apollo object's __typename never lands in the cache item), the small cover photo falls back to
 * the PNG variant the product listings select, and unknown flags are padded to null for the
 * resolver to normalise.
 */
export const buildLocalCartItemInput = ({
  product,
  quantity,
  extraInfo = null,
  minBottlesForFreeShipping,
  standardShippingFee,
}: LocalCartItemInputOptions): { variables: LocalCartAddVariables } => ({
  variables: {
    input: {
      minBottlesForFreeShipping,
      standardShippingFee,
      item: {
        quantity,
        product: {
          id: product.id,
          name: product.name,
          slug: product.slug ? product.slug : "",
          sellingPrice: product.sellingPrice ?? null,
          coverPhotoSmall: product.coverPhotoSmall ?? product.coverPhotoSmallPng ?? null,
          productType: {
            id: product.productType?.id ?? null,
            canCountTowardsOrderMinimum: product.productType?.canCountTowardsOrderMinimum ?? null,
          },
        },
        extraInfo: extraInfo ?? null,
      },
    },
  },
});

/** The "{{product}} added to monthly/one-off box (VIEW BOX)" toast members get for products. */
export const buildGuestProductAddedMessage = (
  productName: string,
  isBox: boolean,
  setShouldOpenBoxAndCartTray: (shouldOpenBoxAndCartTray: boolean) => void
) => (
  <AddToBoxOrCartSuccessMessage
    i18nKey={isBox ? "common:productAddedToMonthlyBox" : "common:productAddedToOneOffCart"}
    values={{ product: productName, interpolation: { escapeValue: false } }}
    setShouldOpenBoxAndCartTray={setShouldOpenBoxAndCartTray}
  />
);

/**
 * Whether a logged-out visitor's add should skip the box-or-one-off tray. `canGuestAddDirectly`
 * is a function, not a value: the tray writes the marker without re-rendering every caller (the
 * full product page has no tray-close callback), so it must be read at click time.
 */
export const useGuestDirectAddCheck = () => {
  const isGuestCheckoutEnabled = useFlag(UNLEASH_GUEST_CHECKOUT);
  const canGuestAddDirectly = useCallback(
    () => isGuestCheckoutEnabled && !isLoggedIn() && hasGuestChosenDirectAdds(),
    [isGuestCheckoutEnabled]
  );
  return { isGuestCheckoutEnabled, canGuestAddDirectly };
};

/**
 * Direct guest adds for non-wine products, the counterpart of the member box/cart mutations in the
 * product controllers: once a guest has answered the box-or-one-off tray, products go straight into
 * the local cart with the same product-named toast members see, framed by their box intent.
 */
export const useGuestProductAdd = (product: GuestAddableProduct | null | undefined) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);
  const { minBottlesForFreeShipping, standardShippingFee } = useAppContext();
  const { setShouldOpenBoxAndCartTray } = useLayoutContext();
  const directAddCheck = useGuestDirectAddCheck();
  const isBlockedByBuildYourOwn = useBuildYourOwnAddGuard();
  const [addItemToLocalShoppingCart, { loading: isAdding }] = useMutation<
    AddItemToLocalShoppingCart,
    LocalCartAddVariables
  >(ADD_ITEM_TO_LOCAL_SHOPPING_CART);

  const addDirectly = async () => {
    if (!product?.id) {
      showErrorAlert(t("common:sorry"));
      return;
    }
    if (product.hasStock === false) {
      showErrorAlert(t("common:noStock"));
      return;
    }
    if (isBlockedByBuildYourOwn([{ product, quantity: 1 }])) return;
    try {
      const result = await addItemToLocalShoppingCart(
        buildLocalCartItemInput({ product, quantity: 1, minBottlesForFreeShipping, standardShippingFee })
      );
      // The client mutates with errorPolicy "all": a resolver failure (e.g. a missing price) can
      // resolve with top-level errors instead of rejecting.
      if (result.errors?.length) throw result.errors[0];
      showSuccessAlert(
        buildGuestProductAddedMessage(product.name, hasGuestBoxIntent(), setShouldOpenBoxAndCartTray),
        {}
      );
    } catch (error) {
      releaseBuildYourOwnReservation([{ product, quantity: 1 }]);
      logError(error as Error, { location: "useGuestProductAdd", action: "ADD_ITEM_TO_LOCAL_SHOPPING_CART" });
      showErrorAlert(t("common:sorry"));
    }
  };

  return { ...directAddCheck, addDirectly, isAdding };
};
